import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const LOAD_TRANSACTION_REQUEST = 'app/DisputeResolutionPage/LOAD_TRANSACTION_REQUEST';
export const LOAD_TRANSACTION_SUCCESS = 'app/DisputeResolutionPage/LOAD_TRANSACTION_SUCCESS';
export const LOAD_TRANSACTION_ERROR = 'app/DisputeResolutionPage/LOAD_TRANSACTION_ERROR';

export const RESOLVE_DISPUTE_REQUEST = 'app/DisputeResolutionPage/RESOLVE_DISPUTE_REQUEST';
export const RESOLVE_DISPUTE_SUCCESS = 'app/DisputeResolutionPage/RESOLVE_DISPUTE_SUCCESS';
export const RESOLVE_DISPUTE_ERROR = 'app/DisputeResolutionPage/RESOLVE_DISPUTE_ERROR';

export const CLEAR_RESOLVED = 'app/DisputeResolutionPage/CLEAR_RESOLVED';

// ================ Reducer ================ //

const initialState = {
  transaction: null,
  // Denormalized related entities from the admin endpoint (listing,
  // customer, provider, booking) keyed by `${type}:${uuid}`.
  included: {},
  // Fresh presigned view URL per S3 photo key (minted server-side).
  photoUrls: {},
  // Keys the server could not sign — rendered as visible errors.
  failedPhotoKeys: [],
  loading: false,
  error: null,
  resolving: false,
  resolved: false,
  resolveError: null,
};

export default function disputeResolutionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LOAD_TRANSACTION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case LOAD_TRANSACTION_SUCCESS:
      return {
        ...state,
        loading: false,
        transaction: payload.transaction,
        included: payload.included,
        photoUrls: payload.photoUrls,
        failedPhotoKeys: payload.failedPhotoKeys,
      };
    case LOAD_TRANSACTION_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    case RESOLVE_DISPUTE_REQUEST:
      return {
        ...state,
        resolving: true,
        resolveError: null,
        resolved: false,
      };
    case RESOLVE_DISPUTE_SUCCESS:
      return {
        ...state,
        resolving: false,
        resolved: true,
        transaction: payload.transaction,
      };
    case RESOLVE_DISPUTE_ERROR:
      return {
        ...state,
        resolving: false,
        resolveError: payload,
      };

    case CLEAR_RESOLVED:
      return {
        ...state,
        resolved: false,
        resolveError: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const loadTransactionRequest = () => ({ type: LOAD_TRANSACTION_REQUEST });
export const loadTransactionSuccess = ({ transaction, included, photoUrls, failedPhotoKeys }) => ({
  type: LOAD_TRANSACTION_SUCCESS,
  payload: { transaction, included, photoUrls, failedPhotoKeys },
});
export const loadTransactionError = e => ({
  type: LOAD_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

export const resolveDisputeRequest = () => ({ type: RESOLVE_DISPUTE_REQUEST });
export const resolveDisputeSuccess = transaction => ({
  type: RESOLVE_DISPUTE_SUCCESS,
  payload: { transaction },
});
export const resolveDisputeError = e => ({
  type: RESOLVE_DISPUTE_ERROR,
  error: true,
  payload: e,
});

export const clearResolved = () => ({ type: CLEAR_RESOLVED });

// ================ Thunks ================ //

export const loadTransaction = transactionId => (dispatch, getState, sdk) => {
  dispatch(loadTransactionRequest());

  // Admin endpoint, NOT the participant-scope SDK: the operator is not
  // a party to the booking (sdk.transactions.show 403'd for every
  // dispute the admin wasn't personally on), and the endpoint also
  // mints fresh presigned URLs for the metadata photo keys in the same
  // round trip (2026-07-02 handoff audit H-2).
  return fetch(`/api/admin/dispute/show?transactionId=${encodeURIComponent(transactionId)}`, {
    method: 'GET',
    credentials: 'include',
  })
    .then(async res => {
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error(body?.error || `Failed to load dispute (${res.status})`);
        err.status = res.status;
        throw err;
      }
      const included = (body?.data?.included || []).reduce((acc, entity) => {
        acc[`${entity.type}:${entity.id?.uuid}`] = entity;
        return acc;
      }, {});
      dispatch(
        loadTransactionSuccess({
          transaction: body?.data?.data,
          included,
          photoUrls: body?.photoUrls || {},
          failedPhotoKeys: body?.failedKeys || [],
        })
      );
      return body;
    })
    .catch(e => {
      dispatch(loadTransactionError(storableError(e)));
      throw e;
    });
};

export const resolveDispute = (transactionId, resolution) => (dispatch, getState, sdk) => {
  dispatch(resolveDisputeRequest());

  const { resolutionType, depositRetainedInSubunits, resolutionNotes } = resolution;

  let transition;
  let params = {
    protectedData: {
      operatorResolution: {
        type: resolutionType,
        notes: resolutionNotes,
        timestamp: new Date().toISOString(),
      },
    },
  };

  if (resolutionType === 'full-refund') {
    transition = 'transition/operator-resolve-partial';
    params.depositRetainedInSubunits = 0;
  } else if (resolutionType === 'partial-retention') {
    transition = 'transition/operator-resolve-partial';
    params.depositRetainedInSubunits = depositRetainedInSubunits;
  } else if (resolutionType === 'full-retention') {
    transition = 'transition/operator-resolve-full';
  }

  const body = {
    transactionId: new UUID(transactionId),
    transition,
    params,
  };

  return fetch('/api/operator-transition', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      dispatch(resolveDisputeSuccess(data.data));
      return data;
    })
    .catch(e => {
      dispatch(resolveDisputeError(storableError(e)));
      throw e;
    });
};